import React, { memo, useEffect, useState, useCallback, useRef } from 'react';
import { Formik, Field, type FormikProps } from 'formik';
import { uniqWith, isMatch, debounce } from 'lodash';

// Types
import type { TrackSubmit, SearchType, FieldType } from './types';
import type { DepartureOption } from '@lt/api/dist/types/departure';
import type { SearchLand } from '@lt/api/dist/methods/search/types';

// Styles:
import UserflowTheme from '@lt/components/themes/userflow';
import { StyledForm } from './styles';

// Context:
import {
  CigaretteProvider,
  type PreviousSearchesOption,
  type RequiredValues,
  type Values,
} from './context';

// Constants:
import {
  INITIAL_VALUES_FORMIK,
  SEARCH_TYPE_NAMES,
  FIELD_NAMES,
  LEFT_BORDER_CLASS,
  storageKeys,
  LAND_PARAMS_COUNT,
} from './constants';

// Components:
import FormDeparturePicker from './components/FormDeparturePicker';
import FormDatePicker from './components/FormDatePicker';
import FormDestinationPicker from './components/FormDestinationPicker';
import FormTouristsPicker from './components/FormTouristsPicker';
import BackWizardButton from './components/BackWizardButton';
import CloseWizardButton from './components/CloseWizardButton';
import SearchButton from './components/SearchButton';
import { useFetchDepartures, useAutocomplete, useSearchLand } from './hooks';
import { buildLandParams } from './utils/buildLandParams';
import { validate } from './validate';
import { getSearchParamsChanges, isEqualPreviousSearches } from './utils';
import { withApiContext } from '../../context/hocs/withApiContext';
import useReadLocalStorageWithDTO from './hooks/useReadLocalStorageWithDTO';
import { savedDepartureDTO } from './utils/savedDepartureDTO';
import { format } from 'date-fns';
import { TripsterLand } from './hooks/useSearchLand';

type CigaretteProps = {
  searchType: SearchType;
  mobileVariant?: 'default' | 'wizard';
  initialValues?: Values;
  onSubmit?: (values: SearchLand) => void;
  host?: string;
  isMainPage?: boolean;
  className?: string;
  setIsWizardOpen?: (newValue: boolean) => void;
  variant?: 'default' | 'hotel';
  openNewTab?: boolean;
  onChange?: (values: Values, changes: Partial<Values>) => void;
  trackSubmit?: TrackSubmit;
  isWidget?: boolean;
  initialOpenedField?: FieldType;
};

const updatePreviousSearches = (
  values: Values,
  searchType: SearchType,
  savedLandParams: PreviousSearchesOption[],
  setSavedLandParams: (searches: PreviousSearchesOption[]) => void
) => {
  /* TODO: handle also presets */
  if (!values.datepicker?.left.date || !values.datepicker?.right.date) {
    return;
  }

  const lastPreviousSearch: PreviousSearchesOption = {
    ...(values as RequiredValues),
    searchType,
    groupName: 'previousSearches',
    value: values.destination?.value,
  };
  // из локалстораджа прилетают строки вместо дат, делаем конвертацию через прогон методов JSON для корректного сравнения ниже
  const lastPreviousSearchWithConvertDate = JSON.parse(JSON.stringify(lastPreviousSearch));
  const newSavedLandParams = [
    lastPreviousSearchWithConvertDate,
    ...(savedLandParams[searchType] || []),
  ];
  const uniqNewSavedLandParamsByType = uniqWith(newSavedLandParams, isEqualPreviousSearches);

  const uniqNewSavedLandParams = {
    ...savedLandParams,
    [searchType]: uniqNewSavedLandParamsByType.slice(0, LAND_PARAMS_COUNT),
  };

  setSavedLandParams(uniqNewSavedLandParams);
};

/**
 * DTO делает следующие преобразования данных:
 * 1) Подогнал начальные даты в полночь
 */
const initialValuesDTO = (initialValues: Values): Values => {
  if (
    initialValues.datepicker &&
    (initialValues.datepicker.left.date || initialValues.datepicker.right.date)
  ) {
    return {
      ...initialValues,
      datepicker: {
        ...initialValues.datepicker,
        left: {
          ...initialValues.datepicker.left,
          date: initialValues.datepicker.left.date
            ? new Date(format(initialValues.datepicker.left.date, 'MM/dd/yyyy'))
            : initialValues.datepicker.left.date,
        },
        right: {
          ...initialValues.datepicker.right,
          date: initialValues.datepicker.right.date
            ? new Date(format(initialValues.datepicker.right.date, 'MM/dd/yyyy'))
            : initialValues.datepicker.right.date,
        },
      },
    };
  }
  return { ...initialValues };
};

const openInNewTab = (redirectUrl: string) => {
  setTimeout(() => {
    window.open(redirectUrl, '_blank');
  });
};

const BaseCigarette = ({
  searchType = SEARCH_TYPE_NAMES.PACKAGE,
  mobileVariant,
  initialValues = INITIAL_VALUES_FORMIK,
  host = window.location.origin,
  onSubmit,
  isMainPage,
  className = '',
  setIsWizardOpen,
  variant = 'default',
  onChange,
  trackSubmit,
  isWidget = false,
  initialOpenedField,
}: CigaretteProps) => {
  /**
   * Данными двумя строчками скидываем время в датах в полночь,потому что мы оперируем датами с полночью
   */
  const formatInitialValues = initialValuesDTO(initialValues);

  const isWizard = mobileVariant === 'wizard';
  const isHotelVariant = variant === 'hotel';
  const isMobile = !!mobileVariant;

  const isPackage = searchType === SEARCH_TYPE_NAMES.PACKAGE;
  const isTrip = searchType === SEARCH_TYPE_NAMES.TRIP;

  const {
    departures,
    loading: departuresLoading,
    popupCityName,
    setPopupCityName,
  } = useFetchDepartures();
  const {
    startOptions,
    fullOptions,
    foundGroupedOptions,
    setParams,
    setDepartureValue,
    savedLandParams,
    setSavedLandParams,
    loading: destinationsLoading,
  } = useAutocomplete(searchType, isWizard);
  const ref = useRef<FormikProps<Values>>(null);

  const { setParams: setSubmitParams, loading: landLoading } = useSearchLand({});
  const savedDeparture = useReadLocalStorageWithDTO<DepartureOption>(
    storageKeys.departure,
    savedDepartureDTO
  );

  const submitHandler = (values: Values) => {
    // сабмит сработает только после валидации, когда все поля будут заполнены
    const landParams = buildLandParams({
      ...(values as RequiredValues),
      host,
      searchType,
    });

    if (trackSubmit) {
      trackSubmit({ values, searchType });
    }

    if (!isTrip && !isHotelVariant) {
      updatePreviousSearches(values, searchType, savedLandParams, setSavedLandParams);
    }

    if (isTrip) {
      const { redirectUrl } = landParams as TripsterLand;
      openInNewTab(redirectUrl);

      return;
    }

    if (onSubmit && !isTrip) {
      onSubmit(landParams as SearchLand);
    } else {
      setSubmitParams(landParams);
    }
  };

  /*
    При отложенной загрузке сигареты пользователь
    может кликнуть в превью кнопки поиска, и в таком
    случае в Formik должно отправляться событие сабмита
    https://formik.org/docs/guides/form-submission
   */
  useEffect(() => {
    if (!ref.current) return;

    if (initialOpenedField === 'submit') {
      ref.current.handleSubmit();
    }
  }, [initialOpenedField]);

  return (
    // Решает конфликты при интеграции на сайт вместе с друой версией lt-components
    <UserflowTheme>
      <CigaretteProvider
        isMobile={isMobile}
        isWizard={isWizard}
        setIsWizardOpen={setIsWizardOpen}
        initialOpenedField={initialOpenedField}
      >
        <Formik
          enableReinitialize={isHotelVariant}
          initialValues={{
            ...formatInitialValues,
            departure: formatInitialValues.departure || savedDeparture || undefined,
          }}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={submitHandler}
          validate={isMobile ? undefined : (values) => validate(values, searchType)}
          innerRef={ref}
        >
          {({ values, initialValues, isValid, ...props }) => (
            <>
              {onChange ? (
                <CigaretteChangeListener
                  values={values}
                  initialValues={initialValues}
                  isValid={isValid}
                  onChange={onChange}
                />
              ) : null}
              <StyledForm
                className={className}
                $isMobile={isMobile}
                $isWizardOpen={isWizard && !!values.destination}
                $isWizard={isWizard}
                $variant={variant}
                $searchType={searchType}
              >
                <Field
                  id={FIELD_NAMES.destination}
                  name={FIELD_NAMES.destination}
                  fullOptions={fullOptions}
                  foundGroupedOptions={foundGroupedOptions}
                  startOptions={startOptions}
                  fetchAutocomplete={setParams}
                  fetchPlaces={setDepartureValue}
                  className={LEFT_BORDER_CLASS}
                  searchType={searchType}
                  variant={variant}
                  isWidget={isWidget}
                  isOpenInitial={initialOpenedField === FIELD_NAMES.destination}
                  as={FormDestinationPicker}
                  isLoading={destinationsLoading}
                />
                {!isTrip && (
                  <Field
                    id={FIELD_NAMES.datepicker}
                    name={FIELD_NAMES.datepicker}
                    className={LEFT_BORDER_CLASS}
                    as={FormDatePicker}
                    searchType={searchType}
                    variant={variant}
                    isOpenInitial={initialOpenedField === FIELD_NAMES.datepicker}
                  />
                )}
                {!isTrip && (
                  <Field
                    id={FIELD_NAMES.tourists}
                    name={FIELD_NAMES.tourists}
                    className={LEFT_BORDER_CLASS}
                    as={FormTouristsPicker}
                    variant={variant}
                    isOpenInitial={initialOpenedField === FIELD_NAMES.tourists}
                  />
                )}
                {isPackage && (
                  <Field
                    id={FIELD_NAMES.departure}
                    name={FIELD_NAMES.departure}
                    value={departures}
                    className={LEFT_BORDER_CLASS}
                    isLoading={departuresLoading}
                    popupCityName={popupCityName}
                    setPopupCityName={setPopupCityName}
                    isMainPage={isMainPage}
                    variant={variant}
                    isWidget={isWidget}
                    isOpenInitial={initialOpenedField === FIELD_NAMES.departure}
                    as={FormDeparturePicker}
                  />
                )}
                <BackWizardButton />
                <CloseWizardButton searchType={searchType} />
                <SearchButton
                  loading={landLoading}
                  shouldDisplay={isTrip && Boolean(values.destination)}
                />
              </StyledForm>
            </>
          )}
        </Formik>
      </CigaretteProvider>
    </UserflowTheme>
  );
};

// https://github.com/jaredpalmer/formik/issues/2769#issuecomment-845235005
type ICigaretteChangeListener = {
  values: Values;
  initialValues: Values;
  isValid: boolean;
  onChange: (values: Values, changes: Partial<Values>) => void;
};

const CigaretteChangeListener = (props: ICigaretteChangeListener) => {
  const { values, initialValues, isValid, onChange } = props;
  const [lastValues, updateState] = useState(values);

  const handleChange = useCallback(
    debounce(
      (values, changes): void => {
        onChange(values, changes);
      },
      500,
      { maxWait: 1500 }
    ),
    []
  );

  useEffect(() => {
    const valuesEqualLastValues = isMatch(values, lastValues);
    const valuesEqualInitialValues = isMatch(values, initialValues);

    if (!valuesEqualLastValues && !valuesEqualInitialValues && isValid) {
      const searchParamsChanges = getSearchParamsChanges(lastValues, values);
      handleChange(values, searchParamsChanges);
    }

    if (!valuesEqualLastValues) {
      updateState(values);
    }
  }, [values, isValid]);

  return null;
};

/** для использования как компонента на сайте */
export const Cigarette = withApiContext<CigaretteProps>(memo(BaseCigarette));

/** для использования как виджета */
export default memo(BaseCigarette);
